// Help-center content model, shared by the client shell (SupportContent) and
// the server routes under app/support/[section]/[topic] — which prerender each
// article so crawlers get real HTML, a unique <title>, and VideoObject JSON-LD
// instead of the identical client-rendered shell every ?topic= URL used to
// return.

// A topic is either a bare title (article still to be written — the page
// renders the "on its way" placeholder) or a written article: `body`
// paragraphs and/or a `video` served from public/.
// One numbered step. An array is a step that runs to more than one paragraph
// (the "No physical receipt? No problem." aside under Upload, for instance).
export type ArticleStep = string | string[];

// A titled group of numbered steps — "Upload", "Sharing", "Paying". Longer
// walkthroughs need this structure; short articles can stay on plain `body`.
export type ArticleSection = {
  heading: string;
  // Numbered steps, for an actual sequence. Rendered as a real <ol>.
  steps?: ArticleStep[];
  // Prose under the heading, for groups that aren't a sequence — "Copy and
  // paste" describes a category, and numbering it would imply an order that
  // isn't there. A section can use either, or both (prose then steps).
  body?: string[];
};

// An ordered run of copy and screenshots, for walkthroughs where a shot has to
// sit BETWEEN two paragraphs. `body` + `image` can't express that — they render
// as all-copy-then-all-media — so articles that need interleaving use `blocks`
// instead and get one explicit source of truth for the order.
export type ArticleBlock =
  | { kind: 'text'; text: string }
  | { kind: 'image'; src: string; alt?: string; width?: number; height?: number };

export type TopicArticle = {
  title: string;
  // Search-facing title, used ONLY for <title>, OpenGraph, and the video's name
  // in structured data. The visible headings and the sidebar keep `title`.
  //
  // The two jobs are different. In the app, "Splitting your first Tabster tab!"
  // is the right label — it's short, it's in the product's voice, and the
  // reader already knows what a tab is. In a search result it's close to
  // useless: nobody types "Tabster tab", so the words a searcher actually used
  // ("split a restaurant bill") appear nowhere in the strongest signal the page
  // has.
  //
  // Deliberately NOT a rename. `topicSlug` derives from `title`, so retitling
  // an article changes its URL and needs a redirect; this leaves URLs, slugs,
  // sidebar labels and in-app copy untouched. Aim for under ~60 characters —
  // Google truncates past that, and the " | Tabster Help" suffix counts.
  seoTitle?: string;
  // Same idea for <meta description>. Without one, the description falls back
  // to the article's opening paragraph, which is written to be read after you
  // have already clicked — not to earn the click.
  seoDescription?: string;
  body?: string[];
  // Ordered copy + screenshots. Rendered after `body`/`sections`; an article
  // normally uses either this or `body`, not both.
  blocks?: ArticleBlock[];
  // Walkthrough content. Rendered after `body`, so an article can open with a
  // plain intro paragraph and then break into steps.
  sections?: ArticleSection[];
  // A still screenshot, for articles where one screen says it better than a
  // clip. Rendered where a video would go; the two aren't mutually exclusive.
  // Width/height are the intrinsic pixel size — same job as the video's, i.e.
  // reserving the box so the page doesn't shift when the image decodes.
  image?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  video?: string;
  poster?: string;
  // Intrinsic video size — used to reserve the aspect ratio before metadata
  // loads (otherwise the player expands from zero height and shifts the page)
  // and to fill VideoObject structured data.
  videoWidth?: number;
  videoHeight?: number;
  // ISO 8601, e.g. 'PT20S'. Google wants a duration on VideoObject.
  videoDuration?: string;
  // ISO date the clip was published — also required by VideoObject.
  videoUploadDate?: string;
  // ISO date (YYYY-MM-DD) this article's CONTENT last meaningfully changed —
  // the words, not the styling. Feeds <lastmod> in the sitemap; falls back to
  // SUPPORT_CONTENT_REVISED when absent.
  //
  // Set this when you rewrite an article, and leave it alone when you refactor
  // the component that renders it. `lastmod` is only worth anything to Google
  // if it's discriminating: a sitemap where every URL claims to have changed on
  // the last deploy is one whose lastmod gets ignored wholesale, which costs
  // the single signal that says which of 61 pages to recrawl first.
  updated?: string;
};

export type TopicItem = string | TopicArticle;

// ── Inline glyphs ────────────────────────────────────────────────────────────
// Body copy can point at a real app icon with a {{token}}, so an article can say
// "tap the ⟨glyph⟩ button" using the same SVG the bottom bar draws instead of
// describing it in words. The article renders the glyph; <meta description>,
// VideoObject JSON-LD and search all run the copy through plainText() first,
// since structured data can't carry markup.
export const INLINE_ICONS: Record<string, { alt: string }> = {
  // The summary (tally) tab in GlobalBottomNav — a receipt with a heart in it.
  'summary-icon': { alt: 'receipt-and-heart' },
};

export const INLINE_ICON_PATTERN = '\\{\\{([a-z0-9-]+)\\}\\}';

// [[Accented text]] renders in the app's cyan ombre, for a lead-in label that
// has to stay inside its sentence (a section heading would put it on its own
// line and drop the colon). Explicit rather than auto-detecting "Label:",
// which would surprise any future copy that happens to contain a colon.
//
// [[Accented text|/href]] makes it a link as well — same ombre, underlined. An
// href may carry a media fragment (#t=20,45) to open another article's video at
// a given point; see ArticleVideo.
export const INLINE_ACCENT_PATTERN = '\\[\\[([^\\]|]+)(?:\\|([^\\]]+))?\\]\\]';

export const plainText = (s: string) =>
  s
    .replace(new RegExp(INLINE_ICON_PATTERN, 'g'), (_m, name) => INLINE_ICONS[name]?.alt ?? '')
    // Metadata and search want the words, without the markers or the href.
    .replace(new RegExp(INLINE_ACCENT_PATTERN, 'g'), (_m, text) => text);

// Every paragraph of a step, flattened.
const stepParagraphs = (step: ArticleStep) => (Array.isArray(step) ? step : [step]);

// All of an article's prose as plain strings — powers search and the
// description fallbacks below, so an article that leads with `sections`
// instead of `body` still gets a real <meta description> rather than the
// templated one.
export const articleParagraphs = (article: TopicArticle): string[] => [
  ...(article.body || []),
  ...(article.sections || []).flatMap((s) => [
    ...(s.body || []),
    ...(s.steps || []).flatMap(stepParagraphs),
  ]),
  ...(article.blocks || []).flatMap((b) => (b.kind === 'text' ? [b.text] : [])),
];

export const articleSummary = (article: TopicArticle): string | undefined => {
  const first = articleParagraphs(article)[0];
  return first ? plainText(first) : undefined;
};

// VideoObject carries the duration as ISO 8601 ('PT69S'); a video sitemap wants
// plain seconds. Derive one from the other rather than authoring both, so a
// re-recorded clip can't end up with a duration that disagrees with itself —
// Google treats a sitemap/markup mismatch as a reason not to index the video.
export const videoDurationSeconds = (article: TopicArticle): number | undefined => {
  const m = /^PT(?:(\d+)M)?(?:(\d+)S)?$/.exec(article.videoDuration || '');
  if (!m) return undefined;
  const secs = Number(m[1] || 0) * 60 + Number(m[2] || 0);
  return secs > 0 ? secs : undefined;
};

export const itemTitle = (item: TopicItem) => (typeof item === 'string' ? item : item.title);
export const itemArticle = (item: TopicItem): TopicArticle | null =>
  (typeof item === 'string' ? null : item);

// What an article calls itself to a search engine, as opposed to in the UI.
// Both the page's <title>/VideoObject and the sitemap's <video:title> read
// through here so they can't drift apart — Google compares the two, and a
// mismatch is a reason to trust neither.
export const searchTitle = (item: TopicItem) => itemArticle(item)?.seoTitle || itemTitle(item);

export const searchDescription = (article: TopicArticle, fallback: string) =>
  article.seoDescription || articleSummary(article) || fallback;

// Partiful-style help-center categories: emoji heading + optional tagline +
// a list of topic subheadings. Each subheading is its own page, reachable
// from the sidebar, the category cards, and search. `short` is the sidebar
// label.
export const SUPPORT_SECTIONS: Array<{
  id: string;
  emoji: string;
  title: string;
  short: string;
  tagline?: string;
  items: TopicItem[];
}> = [
  {
    id: 'getting-started',
    emoji: '🎉',
    title: 'Getting Started',
    short: 'Getting Started',
    items: [
      {
        title: 'Creating your Tabster profile',
        seoTitle: 'Add Payment Methods So Friends Can Pay You Back',
        seoDescription:
          'Set up your profile and link the payment apps you actually use. Friends splitting a bill with you get handed straight to Venmo, PayPal or Cash App with your details already in.',
        body: [
          'Add your name, username, bio, and payment methods. Customize by adding your profile picture and choosing your claimed items design theme.',
          'Payment methods linked will be available to your friends to pay you back.',
          'This is the step that decides how quickly you get paid. When someone taps Pay on a bill you covered, Tabster hands them to whichever app you have linked with your details already filled in. So there is no moment where they have to ask how you want the money.',
          'Link more than one if you use more than one. Different friends default to different apps, and the more overlapping payment methods you have with the friends paying you back, the more flexibility for the group in choosing payment methods. So that one friend who only uses Zelle can pay you back in a couple taps, no questions asked.',
          'Your username is the @handle friends use to tag you in comments, so it is worth picking something recognizable rather than something clever.',
          'Which app a friend gets sent to, and whether the amount is filled in for them, depends on the method. See [[paying with Venmo, PayPal or Cash App|/support/payers-and-payees/paying-with-venmo-paypal-or-cash-app]].',
        ],
        // Clips are re-encoded + kebab-cased by scripts/optimize-faq-video.sh
        // (see the note on that script: sources are ~10x over-bitrate, and the
        // old "FAQ Videos" folder forced %20 escaping into every path).
        video: '/faq-videos/final-profile-setup.mp4',
        poster: '/faq-videos/final-profile-setup-poster.jpg',
        videoWidth: 1080,
        videoHeight: 1920,
        videoDuration: 'PT22S',
        videoUploadDate: '2026-08-01',
      },
      {
        title: 'Adding friends on Tabster',
        seoTitle: 'Add Friends to Split Bills and Trips With',
        seoDescription:
          'Send and accept friend requests so you can drop people straight onto a dinner, trip or expense instead of sending them a link every time.',
        body: [
          'Request & accept friends on Tabster to easily add them on dinners, trips and other expenses.',
          'Being friends is what lets you add someone to a bill directly rather than sending a link and hoping they open it. For people you split with regularly, it is the difference between two taps and a chase.',
          'It also unlocks the running tab between you. Every dinner, trip and expense you share nets into one balance. So to add your roommates and track ongoing roommate expenses, all you have to do is add them as friends on Tabster and start adding expenses. The stories - running tabs - at the top of the homepage will summarize the running total with each roommate or friend, updating as you go. [[Watch how running tabs work here|/support/getting-started/keeping-your-first-running-tab]].',
          'Meanwhile you don’t have to be friends to split receipts or one-off expenses. Anyone can claim their items and pay from a link with no account at all: friends are for the people who keep turning up.',
          'Trips and running tabs are the exceptions: everyone involved needs an account, because trips and running tabs run across days and many receipts. Everyone has to stay identifiable throughout, which is why accounts are required for trips and running tabs. It’s free to have an account and we do not sell your data, so it’s all upside.',
          'Before anyone can pay you back, link the apps you use in [[your Tabster profile|/support/getting-started/creating-your-tabster-profile]].',
        ],
        video: '/faq-videos/adding-friends.mp4',
        poster: '/faq-videos/adding-friends-poster.jpg',
        videoWidth: 1080,
        videoHeight: 1920,
        videoDuration: 'PT27S',
        videoUploadDate: '2026-08-01',
      },
      {
        title: 'Splitting your first restaurant bill',
        seoTitle: 'How to Split a Restaurant Bill with Friends',
        seoDescription:
          'Split a restaurant bill by item in under a minute: snap the receipt, friends tap what they ordered, and everyone pays the person who covered it back.',
        body: [
          'Tabster is how you easily split a restaurant bill with friends and pay back whoever put their card down.',
          'Scan to split now or later - splitting a restaurant bill can take less than 60 seconds.',
          'One person photographs the receipt. If they don’t have a photo, they can also paste a link to the receipt from the restaurant, email in the receipt, or manually add the receipt. Every line item comes back in a tappable interface, so the rest of the table claims what they ordered on their own phones. Six people split a bill in the time it takes one person to read it aloud. Every way of getting a bill in is covered in [[scanning and itemizing a receipt|/support/dinners-getting-paid-back/scanning-and-itemizing-a-receipt]].',
          'Tax and tip are apportioned to what each person claimed, which is the part an even split gets wrong. Whoever had a salad stops subsidising whoever had a steak and three drinks.',
          'The people paying you back do not need the app or an account. They open a link, claim their items, and pay from whichever app they already use.',
          'You can do the whole thing at the table, or leave it half done and finish it later. Every claim is saved as it happens, so the receipt is exactly where the group left it. More on [[splitting a bill at the table or later|/support/payers-and-payees/splitting-a-bill-at-the-table-or-later]].',
          'If everyone had the same thing, [[splitting a bill evenly|/support/payers-and-payees/splitting-a-bill-evenly]] is quicker than claiming item by item. For a shared starter or a bottle of wine, see [[splitting appetizers or other shareables|/support/payers-and-payees/splitting-appetizers-or-other-shareables]].',
          // Internal links out to the commercial pages. Help articles are the
          // topically-relevant pages pointing at /receipts and /trips, which is
          // how those pages earn the authority to compete for the head terms;
          // the articles themselves are never going to rank for "split bills".
          'You can [[split a receipt right now|/receipts]]. No account needed to try it.',
        ],
        video: '/faq-videos/splitting-a-tab.mp4',
        poster: '/faq-videos/splitting-a-tab-poster.jpg',
        videoWidth: 1080,
        videoHeight: 1920,
        videoDuration: 'PT50S',
        videoUploadDate: '2026-08-01',
      },
      {
        title: 'Splitting your first group trip',
        seoTitle: 'How to Split Expenses on a Group Trip',
        seoDescription:
          'Split trip expenses across a whole group: everyone adds their receipts and claims their items, and Tabster nets it down to one payment per person.',
        body: [
          'Create a trip and add your friends on it.',
          'Everyone adds their receipts, claims their items owed, and Tabster calculates the differences.',
          'A trip is one shared tab for the whole holiday rather than a separate split per meal. Hotels, flights, dinners, the rental car and the taxi from the airport all land in the same place.',
          'Anyone on the trip can add to it, so it does not fall to whoever booked everything. Receipts go in as photos, as pasted links, as plain amounts when there was no receipt, or by forwarding a booking confirmation to the trip’s own email address.',
          'At the end Tabster nets the whole thing down to one amount owed per pair of friends, so a week of shared costs finishes in as few payments as possible instead of a tangle of small transfers.',
          'Nothing is fixed until you divvy up, so the running numbers keep moving as people add receipts and claim their items across the trip. You can easily track who owes what on the trip throughout, so if you see one friend is paying for things disproportionately, you can even things out as you go.',
          'You can [[start a group trip|/trips]] and add everyone to it in a few seconds.',
        ],
        video: '/faq-videos/divvying-a-trip.mp4',
        poster: '/faq-videos/divvying-a-trip-poster.jpg',
        videoWidth: 1080,
        videoHeight: 1920,
        videoDuration: 'PT69S',
        videoUploadDate: '2026-08-01',
      },
      {
        title: 'Keeping your first running tab',
        seoTitle: 'How to Split Bills with Roommates Every Month',
        seoDescription:
          'Keep a running tab with each roommate instead of settling up every time. Rent, utilities and recurring bills split automatically and net to one balance.',
        body: [
          'A running tab keeps every bill you split with someone in one place, so you can view all your shared expenses at the top of the homepage in one quick snapshot.',
          "Each friend's story summarizes the remaining difference owed across all trips, receipts, and other expenses. Perfect for tracking roommate costs.",
          'Add recurring group expenses and forward emails with automated splits to easily keep rent, utilities, and other expenses up-to-date in Tabster.',
          'A running tab is the right shape for people you split with constantly. Settling every coffee and every utility bill the moment it happens is a lot of small transfers; letting the balance run and squaring up when it is worth squaring up is fewer.',
          'Because it nets across everything, the number you see is the real position between you. A dinner you covered and a power bill they covered cancel out rather than sitting as two separate debts pointing in opposite directions.',
          'You can set a recurring expense with a predetermined split, like rent split between 3 roommates each month. Rent and utilities that land on the same day each month split themselves and appear on the running tab with each of those roommates without anyone remembering to add them.',
          'Bills that arrive by email can be forwarded to a Tabster address and split on arrival, automating the split for recurring emailed-in expenses like utilities.',
        ],
        video: '/faq-videos/running-tab.mp4',
        poster: '/faq-videos/running-tab-poster.jpg',
        videoWidth: 1080,
        videoHeight: 1920,
        videoDuration: 'PT27S',
        videoUploadDate: '2026-08-01',
      },
      {
        title: 'Posting your first Tabster summary',
        seoTitle: 'Share a Monthly Collage of Your Dinners and Trips',
        seoDescription:
          'At the end of each month Tabster builds a collage from the photos on your receipts and trips, ready to post to your Tabster story or anywhere else.',
        body: [
          'Add a monthly collage of memories to your Tabster story, and across other social media.',
          'Navigate to the summary page through the {{summary-icon}} button in the bottom bar of the homepage.',
          'The collage is built from the photos already attached to the receipts and trips you shared that month, so there is nothing to assemble. It is a by-product of splitting bills all month rather than a separate thing to make.',
          'Nothing posts on its own. The summary is yours to look at, rearrange or ignore, and it only goes anywhere when you choose to share it.',
          'Which is the argument for adding photos to dinners and trips as you go: the collage is only as good as what went into it that month.',
          'More on how it is put together in [[each month you get a Tabster summary collage|/support/monthly-summary/each-month-you-get-a-tabster-summary-collage]].',
        ],
        video: '/faq-videos/tabster-collage.mp4',
        poster: '/faq-videos/tabster-collage-poster.jpg',
        videoWidth: 1080,
        videoHeight: 1920,
        videoDuration: 'PT21S',
        videoUploadDate: '2026-08-01',
      },
    ],
  },
  {
    id: 'dinners-getting-paid-back',
    emoji: '🍕',
    title: "You're Getting Paid Back",
    short: 'Dinners: Getting Paid Back',
    tagline: 'One-off receipts',
    items: [
      {
        title: 'Scanning and itemizing a receipt',
        // Targets the scanning/upload query rather than "how to split a
        // restaurant bill" — that one belongs to "Splitting your first Tabster
        // tab", and two pages chasing the same phrase would split the signal.
        seoTitle: 'How to Scan a Receipt and Split It by Item',
        seoDescription:
          'Photograph a receipt, forward it by email, or paste a delivery link. Tabster reads every line item back with prices so the group can claim what they ordered.',
        // Walkthrough articles like this one open straight into `sections`, so
        // they had no opening line — which left the meta description falling
        // back to step 1 ("Click the + button…"), and left the reader with no
        // orientation. A one-sentence intro fixes both.
        body: [
          'Adding a receipt is how you split a bill in Tabster: upload it, and everyone picks what they ordered.',
          'Once it is in, [[share it with the group|/support/dinners-getting-paid-back/sharing-your-receipt-with-friends-to-select-their-items]] so they can claim their own items, or [[claim on their behalf|/support/dinners-getting-paid-back/claiming-for-friends-and-sending-a-venmo-request]] if they are not around.',
        ],
        sections: [
          {
            heading: 'Uploading',
            steps: [
              'Click the + button on the bottom bar of the homepage and upload a receipt by clicking + New receipt.',
              [
                'Take a photo of the receipt and Tabster will itemize it for you, creating an interactive version for the group.',
                'No physical receipt? No problem. You can also upload from camera roll, add costs manually, email in invoices or other expenses, or paste a link.',
                'On the web, [[upload a receipt to split|/receipts]] without installing anything.',
              ],
            ],
          },
          {
            heading: 'Adding Details',
            steps: [
              // Matches the in-app screen title exactly (see TipScreen).
              "On the It's All in the Details ✨ page, you have the opportunity to add tip if it wasn't on the receipt you uploaded. This ensures the tip will be split proportionally to what everyone selects.",
              'You also can switch the payer & payment method. The default is yourself, but you can also select one of your Tabster friends. Anyone you share the receipt with will see the payment method you’ve selected for the receipt. They will not be able to see other payment methods attached to your or anyone else’s profile unless they are friends on Tabster with the payer.',
              'Set to private or friends. If you set to private, no one except friends added on the receipt or shared on the receipt will be able to see the receipt card on their homepage. Only if everyone on the receipt sets to friends visibility, will all your friends be able to see the receipt card. However, they won’t be able to see the receipt itself unless you add them on it.',
            ],
          },
          {
            heading: 'Sharing',
            steps: [
              'Add any Tabster users who are splitting on the receipt. If you’re not friends yet, this will automatically send a friend request their way, and they will be added once they accept.',
              'For any non-Tabster users who are splitting it, you can share the link or have them scan the QR code. They will be able to select their items and pay without creating an account.',
            ],
          },
          {
            heading: 'Selecting and splitting',
            steps: [
              'Everyone opens the receipt page, and taps to claim items.',
              'Swiping left on an item opens up an option to select a portion of it.',
              'You can also tap an already-selected item to help pay for a portion.',
              'If you want to select on behalf of someone else, you can click the + button top right of the receipt page, and select another user to claim items on their behalf.',
              'If you want to split the receipt evenly, just click the three dot menu on the receipt and click "Quick Split" where you can split between any users on the receipt any number of ways.',
            ],
          },
          {
            heading: 'Paying',
            steps: [
              'Everyone opens the receipt and clicks "Pay" to navigate to the payment summary page which outlines all their owed items and splits.',
              'The payer & payment method is also available on this page in case users want to select a different available payment method. Tabster links out to payment or provides necessary payment info accordingly.',
              'When users navigate back to the app after paying, they are prompted to "mark paid" which updates the running tab at the top of the homepage with the latest payment status.',
            ],
          },
          {
            heading: 'Requesting',
            steps: [
              'If you prefer to Venmo request, you can select a user who owes you in the receipt page by clicking the + button top right and then selecting a user there.',
              'Then click request at the bottom of the page.',
              'Once in the request summary page, click request again.',
              'If your friend has a Venmo linked, it opens Venmo with everything prefilled. If not, it opens Venmo for you to click the user you’re requesting, and then the amount & message are prefilled.',
            ],
          },
        ],
      },
      {
        title: 'Sharing your receipt with friends to select their items',
        seoTitle: 'How to Split a Bill with Friends Who Don’t Have the App',
        seoDescription:
          'Share a receipt by link or QR code and anyone can claim their items and pay without downloading anything or creating an account.',
        body: [
          'You can split a bill with anyone, whether or not they use Tabster: friends you add in the app, or anyone you send a link or QR code to.',
          'What they see when they open it is covered in [[opening a bill someone shared with you|/support/dinners-paying-back/opening-a-bill-someone-shared-with-you]]. To add people permanently rather than per receipt, see [[adding friends on Tabster|/support/getting-started/adding-friends-on-tabster]].',
        ],
        sections: [
          {
            heading: 'Options to share Tabster receipts',
            steps: [
              'Add Tabster friends to the receipt.',
              'Add other users which automatically sends a friend request. Once they accept, they will receive the tab.',
              'Share via link, sending in a text or group text.',
              'Share via QR code, having everyone scan at the table.',
              'Add names of non-Tabster users if you want to select items and request on behalf of friends who don’t have Tabster accounts.',
            ],
          },
        ],
        image: '/faq-videos/share-page.webp',
        imageAlt: "Tabster's share screen: search to add friends, add a non-Tabster name, or share by link or QR code.",
        imageWidth: 886,
        imageHeight: 1164,
      },
      {
        title: 'Claiming for friends and sending a Venmo request',
        seoTitle: 'Split a Bill for Friends Who Are Not There',
        seoDescription:
          'Claim items on someone else’s behalf and send them a Venmo request with the amount, handle and message already filled in. Useful when the table has left.',
        // Copy and screenshot alternate here, so this article is authored as
        // ordered blocks rather than body + image.
        blocks: [
          {
            kind: 'text',
            text: 'Not everyone is going to claim their own items. Somebody leaves early, somebody never opens the link, somebody is not on Tabster at all, so you can do it for them and request the money. Before chasing anyone, it is often quicker to [[nudge or tag them in the comments|/support/payers-and-payees/nudge-or-tag-friends-in-comments]].',
          },
          {
            kind: 'text',
            text: 'To select friends on your receipt to claim items for and Venmo request, click the + button top right and select a user to claim items for.',
          },
          {
            kind: 'image',
            src: '/faq-videos/request-on-behalf.webp',
            alt: 'A Tabster receipt with the + button in the top right circled. That is where you pick which friend to claim items for.',
            width: 886,
            height: 1524,
          },
          {
            kind: 'text',
            text: 'Then click Request, to review items, and Venmo request with the amount, handle, and message pre-filled.',
          },
          {
            kind: 'image',
            src: '/faq-videos/request-review.webp',
            alt: "The request review page for Alejandro: claimed items, surcharges and a $35.99 total, with the Request button at the bottom highlighted.",
            width: 886,
            height: 1692,
          },
          {
            kind: 'text',
            text: 'The request carries the itemized breakdown, so what lands on their phone shows what they ordered rather than a bare figure with no explanation. That is usually the difference between being paid straight away and being asked what it was for.',
          },
          {
            kind: 'text',
            text: 'Their share of tax and tip is worked out the same way as everyone else’s, in proportion to the items you claimed for them.',
          },
          {
            kind: 'text',
            text: 'You can claim for several people on one receipt and request each of them separately, which is the fastest way to close out a table where you were the only one using Tabster.',
          },
        ],
      },
    ],
  },
  {
    id: 'payers-and-payees',
    emoji: '🍣',
    title: 'Tabster for Dinners: Payers & Payees',
    short: 'Both Payers & Payees',
    tagline: undefined,
    items: [
      {
        title: 'Splitting appetizers or other shareables',
        seoTitle: 'How to Split Shared Appetizers on a Bill',
        seoDescription:
          'Split an appetizer, a bottle of wine or any shared item between two or more people: swipe the item left and claim your fraction of it.',
        // Two screenshots in sequence — the swipe, then the picker it opens —
        // so this is authored as blocks (`image` only holds one).
        blocks: [
          {
            kind: 'text',
            text: 'To split one item on a bill between several people, swipe the item left and tap the number selector to select your share.',
          },
          {
            kind: 'image',
            src: '/faq-videos/split-share.webp',
            alt: 'A receipt item swiped left to reveal the "Split _ Ways" number selector.',
            width: 886,
            height: 656,
          },
          {
            kind: 'image',
            src: '/faq-videos/split-ways.webp',
            alt: 'The "Select your share" picker, set to 1/2, with a Claim 1/2 button.',
            width: 886,
            height: 890,
          },
          {
            kind: 'text',
            text: 'The number you pick is your share of that one item, not how many people are splitting it. Choose 1/3 of the calamari and a third of its price comes to you, whoever else claims the rest.',
          },
          {
            kind: 'text',
            text: 'Each person splitting the item does this on their own phone, so nobody has to agree a number up front. Four people claiming a quarter each is four separate taps that happen to add up.',
          },
          {
            kind: 'text',
            text: 'Shares do not have to be equal. Two people can take a third each and two other people can take a sixth each. For ultimate flexibility.',
          },
          {
            kind: 'text',
            text: 'Tax and tip follow the fraction you claimed, so half a starter carries half that starter’s share of the fees. See [[splitting tax and tip on a shared bill|/support/payers-and-payees/splitting-tax-and-tip-on-a-shared-bill]].',
          },
        ],
      },
      {
        title: 'Splitting a bill evenly',
        seoTitle: 'How to Split a Bill Evenly Between Friends',
        seoDescription:
          'Split a bill evenly instead of by item: pick who is splitting it and how many ways, and every share is calculated for you.',
        blocks: [
          {
            kind: 'text',
            text: 'To split a bill evenly instead of item by item, select the Quick Split option in the three dot menu of a receipt.',
          },
          {
            kind: 'image',
            src: '/faq-videos/quick-split.webp',
            alt: "A receipt's three-dot menu with the Quick split option circled.",
            width: 886,
            height: 936,
          },
          {
            kind: 'text',
            text: "Select the users you're splitting with, and choose what portion each user takes.",
          },
          {
            kind: 'image',
            src: '/faq-videos/quick-split-users.webp',
            alt: 'The quick split sheet: user chips to pick who is splitting, a 1/2 share wheel, and a "Claim 1/2 each · 2 people" button.',
            width: 886,
            height: 1116,
          },
          {
            kind: 'text',
            text: 'Quick split is the right tool when the itemized version would not tell you anything useful: a shared bottle of wine, a taxi, a set menu where everybody ate the same thing.',
          },
          {
            kind: 'text',
            text: 'It covers the whole bill including tax and tip, so an even split really is even. Nobody has to work out whether the service charge was already in the number they were given.',
          },
          {
            kind: 'text',
            text: 'You can quick split for a subset of the table rather than everyone. Pick the three people who shared the taxi and it splits between those three.',
          },
          {
            kind: 'text',
            text: 'Splitting evenly is a choice rather than a default here. It is worth taking when itemizing would be pedantic, and worth skipping when the group would rather claim what they actually ordered. That is the case where claiming by item is fairer and takes about as long.',
          },
        ],
      },
      {
        title: 'Splitting tax and tip on a shared bill',
        seoTitle: 'How to Split Tax and Tip on a Shared Bill',
        seoDescription:
          'Tax, tip and service charges are split in proportion to what each person claimed, so nobody overpays on fees for a meal they did not order.',
        body: [
          'Tip, tax and fees are split across the bill in proportion to what each person claimed. Whatever someone selects as a percent of the receipt is automatically allocated to them to help pay back tip, taxes, and any other surcharges.',
          'If the tip on the bill looks wrong, or you are not sure whether it was already included, [[check the original receipt photo|/support/payers-and-payees/checking-the-original-receipt-photo]] before anyone pays.',
          'This is the part an even split gets wrong. Divide a bill four ways and the person who had a salad pays the same tax and tip as the person who had a steak and three drinks: on a large bill that gap is often several pounds.',
          'Claim a fifth of the items and you carry a fifth of the fees. No one has to calculate anything for that to hold.',
          'It applies to every surcharge on the receipt, not just tip: service charge, VAT, delivery, booking fees and the small named extras restaurants add at the bottom are all treated the same way.',
          'The Fees & Totals panel at the foot of the receipt shows each person’s share as it moves, so you can see the arithmetic rather than take it on trust.',
          'If tip was not printed on the receipt, add it during the confirm step when the bill is uploaded and it distributes the same way.',
        ],
        image: '/faq-videos/tip-and-fees.webp',
        imageAlt: 'A receipt with claimed items above a "Fees & Totals: split proportionally to items claimed" panel, showing each person\'s share of tax, tip and total.',
        imageWidth: 886,
        imageHeight: 1450,
      },
      {
        title: 'Splitting a bill at the table or later',
        seoTitle: 'Split a Bill at the Table or Settle Up Later',
        seoDescription:
          'Every claim and split saves in real time, so a group can finish splitting the bill before the card comes back, or leave it half done and pick it up days later.',
        body: [
          'You can settle a bill at the table or days later. Tabster is interactive and saves every claim or split, so as everyone in your group claims items and splits, the receipt progress is saved in realtime.',
          'So you can either complete the entire payment back process in seconds right at the table, or save it for later on knowing the receipt will be exactly how the group left it.',
          'Everyone claims on their own phone at the same time, so a table of six is not waiting on one person to read the bill aloud. You watch the totals move as people tap.',
          'Half-finished is a normal state. If two people claim at dinner and the rest do it on the way home, the receipt is simply waiting when they open it. Nothing expires and nothing resets.',
          'Because the split is saved rather than calculated once and thrown away, the receipt still explains itself weeks later when somebody asks what they actually paid for. The receipt is kept indefinitely as a working, interactive document unless you or someone on the receipt decides to delete it.',
          'If someone settles in cash or by bank transfer instead, [[mark it paid|/support/running-tabs/mark-paid-on-expenses-dinners-and-trips]] so the running tab matches what actually happened.',
        ],
      },
      {
        title: 'Nudge or tag friends in comments',
        seoTitle: 'Remind Friends to Pay Their Share of a Bill',
        seoDescription:
          'Tag someone with an @ in a receipt comment or nudge them from the three dot menu, so the reminder points at what is actually outstanding.',
        body: [
          "You can tag friends with an @ in the comments of a trip or tab card from your homepage. This will give them a notification that they've been tagged in a comment. One example of a tag might be @John we're waiting on your last receipt for dinner yesterday.",
          "You can also use the three dot menu in receipt and trip pages to nudge friends. This will give them a notification that you've nudged them so they will check any outstanding actions.",
          'The difference between the two is how specific you need to be. A nudge is a quiet prompt to go and look at what is outstanding; a comment says the thing out loud and stays on the receipt where everyone can see it.',
          'Both beat a message in the group chat, which everybody reads and assumes is aimed at someone else. A tag lands as a notification on one person.',
          'Comments stay attached to the receipt rather than scrolling away, so the question and the bill it refers to remain together.',
        ],
        // Source arrived as "Tag Comment.PNG" (1125x2436). Converted to webp at
        // 886px to match every other help-center screenshot — 1.1MB PNG down to
        // 86KB, and kebab-cased so the path needs no %20 escaping.
        image: '/faq-videos/tag-comment.webp',
        imageAlt: 'The comments thread on a Tabster receipt, showing a tagged mention: "@domingo make sure to claim the wine!"',
        imageWidth: 886,
        imageHeight: 1918,
      },
      {
        title: 'Paying with Venmo, PayPal or Cash App',
        seoTitle: 'Pay Your Share with Venmo, PayPal or Cash App',
        seoDescription:
          'Tabster hands your share straight to the payment app your friend uses. Which apps prefill the amount and which need a paste depends on what each one allows in a link.',
        body: [
          'Tabster does not hold or move money. It works out what you owe and hands that off to whichever app your friend already gets paid on, so nothing is sitting in a balance waiting to be withdrawn.',
          'How smooth that handoff is comes down to what each payment app permits in a link, which is outside Tabster’s control. The three groups below are the three levels of what is possible.',
        ],
        sections: [
          {
            heading: 'Opens with everything filled in',
            body: [
              'Venmo, PayPal, and Cash App open straight to your friend with the amount already entered. You just confirm and send.',
            ],
          },
          {
            heading: 'Opens to your friend, amount copied',
            body: [
              "Revolut, Monzo, and Lydia open your friend's payment page, but their links can't carry an amount. Tabster copies the amount to your clipboard first, so you just paste it in.",
            ],
          },
          {
            heading: 'Copy and paste',
            body: [
              "Zelle, Bizum, and Wise have no shareable payment link. Tabster shows your friend's details and the amount so you can copy each one and pay from your banking app.",
            ],
          },
        ],
        // Authored as a block rather than `body`: body renders ABOVE the
        // sections, and this has to land after all three of them because it
        // applies to whichever route you took.
        blocks: [
          {
            kind: 'text',
            text: 'After you pay through the above methods, the next time you go back to Tabster you will be prompted to mark the transaction as paid in Tabster. This keeps your running tab with each friend at the top of your homepage, and the status of the homepage cards that are Paid up-to-date for you. [[See how running tabs work in Tabster here|/support/getting-started/keeping-your-first-running-tab]].',
          },
        ],
      },
      {
        title: 'Checking the original receipt photo',
        seoTitle: 'Check the Original Receipt Photo Behind a Split',
        seoDescription:
          'Open the original photo or linked receipt from any split to check a price, confirm whether tip was already included, and correct the itemized version.',
        body: [
          'You can click the receipt icon button in a receipt page, the one left of the three dot menu, to see the original photo or linked receipt source.',
          "You can use this to remember if you added the tip or not and edit Tabster's interactive version of the receipt accordingly.",
          'The source photo is kept with the split rather than discarded after scanning, so the original is always there to check against.',
          'This is what to reach for when a number looks wrong. Zoom into the line in question, compare it against the itemized version, and fix it from Edit Mode if they disagree.',
          'It settles the two arguments that come up most: whether the tip was already on the bill before anyone added one, and whether a service charge is a fee or an item. How those surcharges get shared out is covered in [[splitting tax and tip on a shared bill|/support/payers-and-payees/splitting-tax-and-tip-on-a-shared-bill]].',
        ],
        image: '/faq-videos/view-source.webp',
        imageAlt: 'The Receipt Source sheet showing the original photo of a receipt, with a zoom slider and an Edit Mode button.',
        imageWidth: 886,
        imageHeight: 1768,
      },
    ],
  },
  {
    id: 'dinners-paying-back',
    emoji: '🌮',
    title: "You're Paying a Friend Back",
    short: 'Dinners: Paying Back',
    tagline: 'One-off receipts',
    items: [
      {
        title: 'Opening a bill someone shared with you',
        seoTitle: 'Open a Shared Bill Without Downloading an App',
        seoDescription:
          'Someone sent you a bill to split. Open it from a link or QR code and claim your items without an account, or log in to keep the receipt for later.',
        body: [
          'There are a few ways a bill you are splitting can reach you. Once it is open, the next step is [[claiming the items you ordered|/support/dinners-paying-back/claiming-the-items-you-ordered]].',
          '[[You were sent a link]]: open the link in your browser. From here you can either pay for a receipt logged out, or you can log in for the best experience & the option to save the receipt for future reference.',
          '[[You are shown a QR code]]: scan the code to open the receipt. Same as link, you can claim items and pay logged out, but log in for the best experience & to save.',
          '[[You were added as a friend]]: open your app or log into web and you will see the receipt card on your homepage.',
          "[[You were added as a Tabster user but aren't friends yet]]: you should see a notification to accept the friend request of whoever added you on the receipt, which will automatically add you to the receipt. Accept the friend request either in your notifications page or the friends page.",
        ],
      },
      {
        title: 'Claiming the items you ordered',
        seoTitle: 'Claim What You Ordered on a Shared Bill',
        seoDescription:
          'Tap the items you ordered to claim them and swipe to split anything shared. Selections save as you go, and your share updates at the bottom of the receipt.',
        body: [
          'Tap to claim items and swipe to split. Any selections you make are automatically saved.',
          'Claim only what you actually had. Your running total sits at the foot of the receipt and moves with every tap, so you can see your share before committing to anything.',
          'Anything shared gets swiped rather than tapped. That opens the fraction picker so two of you can take half a starter each. Or if everyone who shared it taps, it will be split between the tappers evenly. The latter approach is easier if this item was an even split. However if you’re selecting 5/6 or some other strange fraction for the item, you should swipe to split.',
          'Nothing is submitted, so there is no wrong order to do this in. Claim something by mistake and tap it again to release it.',
          'You do not need to claim everything in one sitting. The receipt keeps your selections and stays exactly where you left it.',
          'Tax and tip are added to your share in proportion to what you claimed, so the number at the bottom is the real amount you owe rather than a subtotal to adjust later. The detail is in [[splitting tax and tip on a shared bill|/support/payers-and-payees/splitting-tax-and-tip-on-a-shared-bill]].',
          'When you are done, [[review your amount owed and settle up|/support/dinners-paying-back/reviewing-your-amount-owed-and-settling-up]].',
          // Timestamps track the cut of splitting-a-tab.mp4 — "Everyone taps to
          // claim" lands at :23 and the swipe-to-split sequence ends at :38.
          // Re-check these whenever the clip is re-recorded.
          "Review [[:23s to :38s of the first Tabster tab video|/support/getting-started/splitting-your-first-restaurant-bill#t=23,38]] if you want to see how it's done.",
        ],
      },
      {
        title: 'Reviewing your amount owed and settling up',
        seoTitle: 'Check What You Owe and Pay Your Share',
        seoDescription:
          'The payment summary breaks your share down line by line: items, splits and your portion of tax and tip. Then it hands you to Venmo, Cash App or your bank.',
        // Authored as blocks rather than `body` so the screenshot can sit
        // between two paragraphs. `image` renders at one fixed position after
        // the prose, which would have put the pay page at the foot of the
        // article instead of under the sentence that introduces it.
        blocks: [
          {
            kind: 'text',
            text: 'To pay your share of the bill, click the Pay button at the bottom of a receipt page. From there, you will be brought to the payment summary page.',
          },
          {
            kind: 'image',
            src: '/faq-videos/pay-page.webp',
            alt: 'The Tabster payment summary page: the payer and their Venmo handle at the top, claimed items priced line by line, surcharges for tax and a culinary fee, the total owed, and a Pay button.',
            width: 886,
            height: 1918,
          },
          {
            kind: 'text',
            text: "Check your items and claimed splits, and the fees & surcharges split automatically in proportion to how much you've claimed.",
          },
          {
            kind: 'text',
            text: 'The breakdown is itemized rather than a single figure, so you can see exactly what you are paying for. That is the difference between being told you owe $35.99 and being shown why.',
          },
          {
            kind: 'text',
            text: 'Then review the payer and payment method at the top. Clicking pay will either link you directly to the payment platform of choice (i.e. Venmo, Cash App) or give you the information you need to complete payment (i.e. Zelle).',
          },
          {
            kind: 'text',
            text: 'Check the payer is right before you send. It is whoever actually put their card down, and it is the one detail worth a second of attention, because a payment sent to the wrong person is the one mistake Tabster cannot undo for you.',
          },
          {
            kind: 'text',
            text: 'If a number looks wrong, open the original receipt photo from the icon left of the three dot menu and compare before paying.',
          },
          {
            kind: 'text',
            text: 'Paying does not delete the receipt. It stays on your homepage with the full breakdown, so you can look up months later what a given dinner actually cost you. You will be prompted to mark the receipt as paid the next time you open the app, so it shows as paid on your homepage and in your running tab with the friend you paid back in the stories section of the homepage. This is just for your record keeping and does not delete or freeze the receipt.',
          },
        ],
      },
    ],
  },
  {
    id: 'trips',
    emoji: '🌴',
    title: 'Tabster for Trips',
    short: 'Trips',
    tagline: 'Divvying up several receipts',
    items: [
      {
        title: 'Creating a trip',
        seoTitle: 'How to Split Trip Expenses with a Group',
        seoDescription:
          'Create a shared trip, add the friends coming, and collect every expense in one place, so a week away ends in one payment each instead of a dozen.',
        body: [
          'A trip is one shared tab for a whole holiday. Instead of splitting each dinner, hotel and taxi as it happens, everything lands in the same place and gets worked out once at the end.',
          'Click the + button on the bottom bar of the homepage. Choose trip expenses: start a trip or add to one. Name the trip and add the friends joining.',
          'Everyone adds receipts to it as you go, instead of splitting every meal separately and trying to remember who covered what.',
          'Use a trip whenever more than one person is going to pay for something. A single receipt is the right tool for one dinner; a trip is the right tool for a weekend where four different people put their card down at different moments.',
          'Next: [[get everyone onto the trip|/support/trips/everyone-creates-an-account-to-be-added-on-the-trip]], then [[everyone adds their receipts|/support/trips/everyone-adds-their-receipts]] as you go. At any point you can [[check the running differences owed|/support/trips/viewing-running-differences-owed-in-a-trip]].',
          'There is no limit on how many people or receipts a trip can hold, and it’s 100% free just like everything on Tabster.',
          '[[Watch how it’s done here|/support/getting-started/splitting-your-first-group-trip]]. On the web you can [[create a trip and split expenses|/trips]] straight from your browser.',
        ],
      },
      {
        title: 'Viewing running differences owed in a trip',
        seoTitle: 'See Who Owes What During a Group Trip',
        seoDescription:
          'Open a trip at any point to see the running balance for each person: what they paid measured against what they claimed, updating as receipts are added.',
        body: [
          'Open the trip at any point to see where everyone stands. Tabster nets what each person paid against what they claimed, so you get one running difference per person instead of a pile of separate debts.',
          'On the trip page, the number under each person is the difference owed between you and them. Someone who paid for the villa and ate cheaply all week shows a large figure for what you owe them; someone who paid for nothing but joined every dinner shows a large amount for what they owe you. This is similar to the numbers at the top of the homepage, but calculated at just the trip level rather than across all your historical expenses with each friend.',
          'It recalculates every time anyone adds a receipt or claims an item on the trip, so what you are looking at is always up-to-date.',
          'This is the part that removes the mid-trip "are we square?" conversation. Anyone can check the trip themselves rather than asking whoever has been keeping track.',
          'The numbers only settle once everyone has finished. See [[everyone claims their items and receipt shares|/support/trips/everyone-claims-their-items-and-receipt-shares]] and [[divvying up at the end of the trip|/support/trips/divvying-up-at-the-end-of-the-trip]].',
        ],
        // Source arrived as "Trip Running.PNG" (1125x2436, 1MB). Converted to
        // webp at 886px like every other help-center screenshot — 85KB — and
        // kebab-cased so the path needs no %20 escaping.
        image: '/faq-videos/trip-running.webp',
        imageAlt: 'A Tabster trip page with the running difference shown under each person, "you owe $468" highlighted beneath one member, above the trip’s receipts and how much of each is claimed.',
        imageWidth: 886,
        imageHeight: 1918,
      },
      {
        title: 'Everyone creates an account to be added on the trip',
        seoTitle: 'Share a Group Trip So Everyone Can Add Expenses',
        seoDescription:
          'Send a trip by link or QR code so everyone on the holiday can add their own receipts and claim their own items, instead of it falling to one organizer.',
        body: [
          'Share the trip the same way you share a receipt: add Tabster friends directly, send the link, or have people scan the QR code.',
          'Everyone on the trip can add receipts and claim their own items, so it does not all fall on whoever started it.',
          'The only difference is you need a Tabster account in order to be added on a trip.',
          'That account requirement is the one place a trip is stricter than a single receipt, where a guest can claim and pay from a link with no signup. A trip runs for days and everyone needs to be identifiable across every receipt in it, which a one-off guest session cannot do.',
          'It is worth getting everyone in on the first day rather than the last. People add receipts as they go when the trip is already on their phone, and reconstructing a week from memory afterwards is exactly the work a trip is meant to remove.',
          // :10–:14 of divvying-a-trip.mp4 is the "Share with the group"
          // segment — the share sheet and QR code. Ends at :14, not :15: the
          // clip cross-fades to the "Everyone adds receipts" title card on :15,
          // and since playback PAUSES on the end mark that card would be the
          // frame left on screen. Re-check if the clip is ever re-recorded.
          '[[Watch how friends open trips here|/support/getting-started/splitting-your-first-group-trip#t=10,14]].',
        ],
      },
      {
        title: 'Everyone adds their receipts',
        seoTitle: 'Add Hotel, Flight and Dinner Receipts to a Trip',
        seoDescription:
          'Photograph receipts as you go, forward booking confirmations to the trip’s own email address, or enter a cost by hand when there is no receipt at all.',
        body: [
          'Anyone on the trip can add to it. Open the trip, click Add receipts, and upload a photo, paste a link, or enter the cost manually.',
          'Bulk upload takes several photos at once, which is the quickest way to clear a backlog of receipts that piled up over a few days.',
          'Trips also have their own Tabster email address. Forward confirmations straight to it and they get added for you, which is the easiest way to handle hotels, flights and rentals that only ever arrive by email. You can also just screenshot emailed invoices or costs listed in an email, and upload the screenshot as a photo to add the expense.',
          'Anything with no receipt goes in as a plain amount: a taxi, a tip, a round bought at the bar. It splits like everything else, the same way [[group expenses|/support/roommates/add-group-expenses-one-off-or-recurring-costs]] work for household costs.',
          'Each upload gets a quick Review and Confirm step where you check who paid and add tip if it was not on the receipt. Getting the payer right matters more than anything else on that screen, because that is who the rest of the trip pays back. Although you can always edit the receipt later to update the payer.',
        ],
        // Source arrived as "Review Confirm.PNG" (1125x2436, 1.2MB). Converted
        // to webp at 886px like every other help-center screenshot — 79KB — and
        // kebab-cased so the path needs no %20 escaping. Illustrates the final
        // paragraph, which is where `image` renders, so no blocks needed here.
        image: '/faq-videos/review-confirm.webp',
        imageAlt: 'The Review & Confirm screen for a bulk upload: each receipt listed with an Add Tip button and editable payer and payment method, above a Save button.',
        imageWidth: 886,
        imageHeight: 1918,
      },
      {
        title: 'Everyone claims their items and receipt shares',
        seoTitle: 'Claim Your Items Across Every Trip Receipt',
        seoDescription:
          'Work through the trip receipt by receipt claiming what you had: tap to claim, swipe to split a shared item, then confirm you are done so the trip can be divvied up.',
        body: [
          'Open each receipt in the trip and claim what you had, exactly like a single tab: tap to claim, swipe to split.',
          'The trip tracks how far through you are, so you can stop after two receipts and pick the rest up later without losing your place.',
          'From the trip page you can also swipe to split an entire receipt, which is quicker than claiming line by line for something like a villa or a rental car that everyone shared equally.',
          'When you have been through them all, confirm with "I’ve claimed all my items across receipts" so the group knows you are done and the trip can be divvied up.',
          'You can also click the + button in a trip receipt to change who you’re claiming on behalf of. This can help things move more quickly, if your friend is being lazy about claiming their items but you want to divvy up soon without waiting for them.',
          // :44–:47 is the "Every trip member" segment — the + button is up in
          // the receipt header and items are claimed across receipts for
          // different people. Ends at :47, not :48: playback PAUSES on the end
          // mark and :48 has already cross-faded to "Opens the trip to claim".
          '[[Watch someone claim items|/support/getting-started/splitting-your-first-group-trip#t=44,47]] for each member of the trip by using the + button in trip receipts here.',
        ],
      },
      {
        title: 'Divvying up at the end of the trip',
        seoTitle: 'How to Settle Up After a Group Trip',
        seoDescription:
          'Tabster nets a whole trip down to one amount owed per pair of friends, so a week of shared costs ends in as few payments as possible rather than a tangle of small transfers.',
        body: [
          'Once everyone has added their receipts and claimed their items, the trip is ready to divvy up.',
          // The divvy-up sequence is the tail of divvying-a-trip.mp4: :63 is the
          // first frame with the Divvy up button up (:62 is still the cross-fade
          // into it), and the netted breakdown runs to the end.
          'See [[:63s to :68s|/support/getting-started/splitting-your-first-group-trip#t=63,68]] of the trip tutorial video to see how divvying up works.',
          // "per pair" is the accurate description of the netting and matches
          // the /trips landing copy — an earlier version of this paragraph said
          // "one amount per person", which describes a different calculation.
          'Tabster nets the whole trip down to the fewest payments possible, so instead of ten separate paybacks you get one amount owed per pair of friends. From there you can pay or request right from the trip, or copy the full breakdown to send to the group.',
          'Netting is why the total number of payments stays small. If you owe Alejandro and Alejandro owes Klara, Tabster cancels what it can rather than sending three people chasing each other around the group chat.',
          // Confirmed behavior: unclaimed items are NOT redistributed across the
          // group — an early draft said they were, which would have told readers
          // they get charged for items nobody claimed. There are two real routes
          // past a straggler, and a later claim opens a SEPARATE divvy payment
          // rather than reopening one already marked complete.
          'You do not have to wait for a straggler. If someone has not finished claiming, [[nudge or tag them on the trip card|/support/trips/nudge-or-tag-friends-in-comments-on-the-trip-card]], or divvy up anyway. You can claim items on their behalf using the + button in trip receipts. Or you can divvy up what’s been claimed, and then mark those payments as complete. Anything selected after payments are complete will be separated into different divvy payments. So you can easily keep track of what’s remaining.',
          'Amounts stay visible after you divvy up, so anyone who pays later can still see exactly what they owe and to whom.',
          'If somebody settles in cash instead, [[mark it paid|/support/running-tabs/mark-paid-on-expenses-dinners-and-trips]]. Anything still outstanding keeps running on [[your tab with that friend|/support/running-tabs/running-tabs-work-with-any-friend]].',
        ],
      },
      {
        title: 'Nudge or tag friends in comments on the trip card',
        seoTitle: 'Nudge Friends to Claim Their Trip Expenses',
        seoDescription:
          'Tag someone with an @ in a trip comment, or nudge from the three dot menu, when the trip is waiting on one person before it can be divvied up.',
        body: [
          'Tag someone with an @ in the comments on a trip card and they will get a notification, exactly like on a tab.',
          'You can also nudge from the three dot menu on the trip page. Useful when the trip is waiting on one person’s receipts or claims before it can be divvied up.',
          'A nudge points at the specific thing that is missing, which tends to work better than a message to the whole group that everyone assumes is meant for somebody else.',
          'Comments live on the trip itself rather than in a separate thread, so the question and the trip it is about stay together. Six months later the trip still explains itself.',
        ],
        // Source arrived as "Nudge Friends.PNG" (1125x2436, 456KB). Converted to
        // webp at 886px like every other help-center screenshot — 43KB — and
        // kebab-cased so the path needs no %20 escaping.
        image: '/faq-videos/nudge-friends.webp',
        imageAlt: 'The three dot menu on a trip receipt, with Nudge friends at the top of the list above Quick split, Edit tab, View source and Delete.',
        imageWidth: 886,
        imageHeight: 1918,
      },
    ],
  },
  {
    id: 'roommates',
    emoji: '🏡',
    title: 'Tabster for Roommates',
    short: 'Roommates',
    tagline: 'Ongoing group expenses',
    items: [
      {
        title: 'A running tab with each roommate',
        seoTitle: 'Track What You and Your Roommates Owe Each Other',
        seoDescription:
          'A running tab with each roommate collects rent, bills, groceries and dinners into one balance, so you always know the current position without adding anything up.',
        body: [
          'Tabster keeps a running tab with every friend, so shared costs collect in one place. This is perfect for roommates who you split rent, utilities, dinners, trips, or any other expenses with on a consistent basis.',
          'Each friend or roommate’s story sits at the top of your homepage with the current difference owed. Tap one to see every dinner, bill and expense behind that number.',
          'The number is the net difference owed between the two of you. You covered the internet, they covered groceries, and what shows is the difference left over.',
          'That is the whole point of letting it run. Settling every shared cost the moment it happens means a dozen small transfers a month; letting them offset means one payment when it is worth making one.',
          'The costs themselves get in three ways: [[emailed bills|/support/roommates/splitting-utility-and-other-bills-or-receipts-by-email]], [[group expenses with no receipt|/support/roommates/add-group-expenses-one-off-or-recurring-costs]], and [[recurring ones that repeat on their own|/support/roommates/set-recurring-group-expenses]].',
          'Nothing needs adding up at the end of the month, because the answer is already in the top row of your homepage, updating as you and your friends mark things as paid or add different expenses.',
        ],
        image: '/faq-videos/roommate-story.webp',
        imageAlt: "A friend's story: rent, a trip, an emailed bill and a dinner listed with paid and unpaid amounts, a running total, and a Pay button.",
        imageWidth: 886,
        imageHeight: 1790,
      },
      {
        title: 'Splitting utility and other bills or receipts by email',
        seoTitle: 'How to Split Utility Bills with Roommates',
        seoDescription:
          'Forward a power, internet or water bill to your Tabster address and it is read, split between your roommates and added to everyone’s tab for you.',
        body: [
          'Click the + button on the bottom bar of the homepage and choose Email bills. Set who is splitting it and how, then generate an email address for it.',
          'Forward a bill to that address and Tabster reads the invoice and splits it for you. Best for anything that arrives every month: power, internet, water. Set up a forwarding rule in your email and they will land in Tabster on their own.',
          'A forwarding rule is what turns this from a task into nothing. Point it at the address once and the bill is read, split and on everyone’s tab before you have opened it yourself.',
          'The amount is taken from the invoice, so a bill that changes every month splits correctly every month without anyone re-entering a figure.',
          'This covers the household costs that only ever arrive as email and never as a receipt, which tends to be the ones that go unsplit for longest.',
        ],
        image: '/faq-videos/email-in-bills.webp',
        imageAlt: 'The Auto-split by email sheet: choose who paid, set the split between people, then generate an email address to forward bills to.',
        imageWidth: 886,
        imageHeight: 1054,
      },
      {
        title: 'Add group expenses (one-off or recurring costs)',
        seoTitle: 'Split Rent and Shared Costs With No Receipt',
        seoDescription:
          'Rent, a cleaner, a shared subscription: add any cost that never comes with a receipt, choose who splits it and how, and it lands on everyone’s tab.',
        body: [
          'For costs that never come with a receipt, like rent, a cleaner or a shared subscription, click the + button on the bottom bar and choose Group expense. Name it, enter the amount, and pick who is splitting it and how.',
          'It shows up as a card on everyone’s homepage and rolls into your running tabs like any other expense.',
          'Splits do not have to be equal. The person with the bigger bedroom can take a larger share of rent, and whoever does not use the streaming subscription can be left off it entirely.',
          'For anything that comes back every month, [[set it to recur|/support/roommates/set-recurring-group-expenses]] rather than adding it again each time.',
          'This is the catch-all for the household costs that never produce anything to photograph, which is most of the expensive ones.',
          'Once it is in, it behaves like every other expense. It nets against what your roommates have covered rather than sitting as a separate thing to remember.',
        ],
        // Moved here from "Set recurring group expenses": this shot is the
        // Split an expense sheet as a whole — name, amount, payer, split — which
        // is what THIS article describes. Its repeat control reads "Does not
        // repeat", so on the recurring article it was showing the feature
        // switched off.
        image: '/faq-videos/recurring-expense.webp',
        imageAlt: 'The Split an expense sheet: expense name and amount, a date and repeat control, who paid, and a percentage split between two people.',
        imageWidth: 886,
        imageHeight: 1214,
      },
      {
        title: 'Set recurring group expenses',
        seoTitle: 'Split Rent and Monthly Bills Automatically',
        seoDescription:
          'Set a shared cost to repeat and Tabster creates it each time it falls due, split the same way, so rent and monthly bills land on everyone’s tab without being re-entered.',
        body: [
          'When you add a group expense you can set it to repeat: monthly rent, a weekly cleaner, whatever the cadence.',
          'Tabster creates a fresh expense each time it comes due, split the same way, so you set it up once and stop thinking about it. Editing the series changes future ones and leaves past ones as they were.',
          'That last part matters more than it sounds. When rent goes up, you change the series from that point on and the previous months stay as they were actually paid. So the history remains a record rather than being rewritten.',
          'Each occurrence appears as its own card when it falls due, so a month everyone paid is visibly distinct from a month still outstanding.',
          'Between recurring expenses and [[emailed-in bills|/support/roommates/splitting-utility-and-other-bills-or-receipts-by-email]], most of a household’s fixed monthly costs end up splitting themselves with nobody doing anything each month. They all land on [[one balance per roommate|/support/roommates/every-shared-cost-lands-on-one-balance]].',
        ],
        // Source arrived as "Recurring.PNG" (1125x2436, 603KB). Converted to
        // webp at 886px like every other help-center screenshot — 38KB — and
        // kebab-cased so the path needs no %20 escaping. Shows the repeat
        // dropdown OPEN with the cadences listed, which is the feature this
        // article is about; the previous shot had it set to "Does not repeat".
        image: '/faq-videos/recurring.webp',
        imageAlt: 'The repeat dropdown open on the Split an expense sheet, listing Does not repeat, Daily, Weekly on Sunday, Every 2 weeks on Sunday, Monthly on the 2nd, Annually on August 2, and Custom.',
        imageWidth: 886,
        imageHeight: 1918,
      },
      {
        title: 'Every shared cost lands on one balance',
        seoTitle: 'One Balance for Every Shared Cost With a Roommate',
        seoDescription:
          'Dinners, trips, emailed bills and recurring expenses all net into a single running tab per person, so there is one number to look at rather than five places to check.',
        body: [
          'Dinners, trips, emailed-in bills and recurring expenses all feed the same running tab with each person.',
          'So you never have to work out who owes what across five different places. The story at the top of your homepage is always the current answer.',
          'A meal out and the electricity bill are the same kind of thing once they are on the tab. One nets against the other instead of living in a different app or a different conversation.',
          'It is the difference between knowing your position with someone and having to reconstruct it. Reconstructing is what people give up on, and giving up is how shared costs quietly stop being even.',
          'When you do settle, you are settling one number rather than working through a list.',
        ],
        // Source arrived as "Summary stories.PNG" (1125x2436, 3.0MB). Encoded at
        // quality 74 rather than the usual 82 — this one contains a photograph
        // rather than flat UI, so it was 194KB at the default. 74 saves 46KB
        // with the story-row labels still sharp (checked at 1:1).
        image: '/faq-videos/summary-stories.webp',
        imageAlt: 'The Tabster homepage with the story row circled at the top, friend avatars each showing a running balance, "you owe $468" and "you owe $120", above a shared trip card.',
        imageWidth: 886,
        imageHeight: 1918,
      },
    ],
  },
  {
    id: 'running-tabs',
    emoji: '🧾',
    title: 'Tabster Running Tabs',
    short: 'Running Tabs',
    tagline: undefined,
    items: [
      {
        title: 'Running tabs work with any friend',
        seoTitle: 'Keep a Running Tab With Any Friend You Split With',
        seoDescription:
          'Every friend on Tabster has a running tab that fills itself as you split dinners, trips and expenses. Nothing to set up and nothing to maintain.',
        body: [
          'Running tabs are not just for roommates. Every friend on Tabster has one, and it fills up on its own as you split dinners, trips and expenses together.',
          'There is nothing to set up. The tab exists as soon as you are friends.',
          'It is worth having with anyone you split with more than occasionally, not only people you live with. The friend you always get dinner with, a sibling, a partner. Anyone where costs go both ways over time. For housemates specifically, see [[a running tab with each roommate|/support/roommates/a-running-tab-with-each-roommate]].',
          'Rather than settling every single thing, [[let the balance run|/support/running-tabs/keep-a-running-tab-of-ongoing-expenses]] and square up when it is worth squaring up.',
          'Because it fills itself, it is accurate without anyone maintaining it. No one has to remember to log the round they bought or the taxi they covered for it to count.',
          'Watch [[the running tab video|/support/getting-started/keeping-your-first-running-tab]] to see one build up over a month.',
        ],
      },
      {
        title: 'Keep a running tab of ongoing expenses',
        seoTitle: 'Let a Balance Run Instead of Settling Every Time',
        seoDescription:
          'A running tab tracks the difference between what you have covered and what a friend has, so small amounts offset instead of becoming constant transfers.',
        body: [
          'Instead of settling every single thing, let it run. The tab tracks the difference between what you have covered and what they have.',
          'Tap a friend’s story at the top of the homepage to see every dinner, trip and expense sitting behind that number.',
          'Letting it run is usually the better default with anyone you see often. Sending $8 back and forth every few days is a lot of transfers to arrive at roughly where you started.',
          'When something does get paid outside the app, [[mark it paid|/support/running-tabs/mark-paid-on-expenses-dinners-and-trips]] so the balance stays true.',
          'The tab is a difference rather than a debt, and it moves in both directions. Cover the next dinner and it swings your way without anyone doing anything about it.',
          'The detail stays available underneath, so letting it run never means losing track of what it is made of.',
        ],
      },
      {
        title: 'Mark paid on expenses, dinners and trips',
        seoTitle: 'Record a Payment Made in Cash or by Bank Transfer',
        seoDescription:
          'Mark a dinner, trip or expense as paid when someone settles outside the app, so the running tab reflects what has actually changed hands.',
        body: [
          'When someone pays you back outside Tabster in cash, by bank transfer or any other way, mark it paid so the running tab matches reality.',
          'After you pay through Tabster you will be asked whether it went through. Marking it paid updates the tab for both of you.',
          'Tabster does not move money, so it cannot see a bank transfer or a note handed over at the table. Marking paid is how it learns that something settled elsewhere.',
          'It updates for both people rather than just the person who marked it, so there is one version of what has been paid instead of two accounts that disagree.',
          'This is the habit worth keeping. A tab that quietly drifts from reality is one nobody trusts, and an untrusted balance gets abandoned in favor of guessing.',
        ],
        // Source arrived as "Mark paid.PNG" (1125x2436, 1.5MB). Converted to
        // webp at 886px like every other help-center screenshot — 80KB — and
        // kebab-cased so the path needs no %20 escaping.
        image: '/faq-videos/mark-paid.webp',
        imageAlt: 'The confirmation prompt on the Tabster homepage, "Did you pay Justin for Alpino Vino?" with the amount owed and Not yet / Mark paid buttons, above the story row of running balances.',
        imageWidth: 886,
        imageHeight: 1918,
      },
    ],
  },
  {
    id: 'monthly-summary',
    emoji: '🖼️',
    title: 'Your Monthly Tabster Summary',
    short: 'Monthly Summary',
    tagline: 'Post to Tabster and other socials',
    items: [
      {
        title: 'Each month you get a Tabster summary collage',
        seoTitle: 'Get a Monthly Recap of Your Dinners and Trips',
        seoDescription:
          'At the end of every month Tabster builds a collage from the receipts, trips and photos you shared, waiting on your summary page.',
        body: [
          'At the end of each month Tabster builds Your Tabster Summary: a collage from the receipts, trips and photos you shared that month.',
          'Find it through the {{summary-icon}} button in the bottom bar of the homepage.',
          'It builds itself from what is already there. Splitting bills all month is what produces it, so there is nothing to make and nothing to remember to do.',
          'It arrives whether or not you do anything with it. Plenty of people just look at it, which is a fine use of it.',
          'From there you can [[change which photos are in it|/support/monthly-summary/customizing-the-summary-collage]], [[post it to your Tabster story|/support/monthly-summary/sharing-a-collage-to-your-tabster-story]], or [[share it anywhere else|/support/monthly-summary/sharing-a-collage-to-your-socials]]. Older ones stay on [[your summary page|/support/monthly-summary/viewing-previous-months-collages]].',
          'Watch [[the collage video|/support/getting-started/posting-your-first-tabster-summary]] to see one built and shared end to end.',
        ],
      },
      {
        title: 'Customizing the summary collage',
        seoTitle: 'Swap, Crop and Rebuild Your Monthly Collage',
        seoDescription:
          'Rebuild the collage with different photos, swap any single photo for another from that month, or crop it. Nothing posts until you decide to share it.',
        body: [
          'The collage fills itself in, but you are not stuck with the first version. Refresh collage rebuilds it with new photos. You can also click on an individual photo to swap it out for another from that month, or crop it. Remember your collage is only as fun as the photos you’ve added that month to dinners and trips. 🤩',
          'Nothing is posted until you choose to share it, so refresh as many times as you like.',
          'Refresh changes the whole set; tapping a single photo changes just that one. Use the second when the collage is nearly right and one picture is letting it down.',
          'It only draws on photos from that month, so what you can swap in depends on what got added to dinners and trips while they were happening.',
        ],
      },
      {
        title: 'Sharing a collage to your Tabster story',
        seoTitle: 'Post Your Monthly Collage to Your Tabster Story',
        seoDescription:
          'Add the collage to your Tabster story and friends see it at the top of their homepage, shared with the people who were actually there.',
        body: [
          'Click Add to story to post the collage to your Tabster story, where friends see it at the top of their homepage.',
          'This is how the month gets shared back to the people who were actually there for it.',
          'A Tabster story goes to your friends rather than to a public feed, which is a different audience from anywhere else you might post it.',
          'It sits alongside their running tabs at the top of the homepage, so it turns up where they already look rather than needing to be sought out.',
        ],
      },
      {
        title: 'Sharing a collage to your socials',
        seoTitle: 'Share Your Collage to Instagram or Anywhere Else',
        seoDescription:
          'Send the collage out of Tabster through your phone’s share sheet. It shares as an image, so it works as a story post on any platform.',
        body: [
          'You can also send the collage out of Tabster through your phone’s share sheet: Instagram, Messages, anywhere else you post.',
          'It shares as an image, so it works as a story post on any platform.',
          'Because it is just an image, nothing is required of whoever sees it. Friends without Tabster see the collage exactly as you do.',
          'The share sheet is your phone’s own, so anywhere you can normally send a photo is somewhere you can send this.',
        ],
      },
      {
        title: "Viewing previous months' collages",
        seoTitle: 'Look Back at Previous Months’ Collages',
        seoDescription:
          'Past collages stay on your summary page, so you can scroll back through earlier months whenever you want.',
        body: [
          'Past months stay on your summary page, so you can scroll back through them whenever you want.',
          'Worth a look at the end of the year, when you can see everywhere you went and everyone you went with.',
          'Nothing expires. A month you never got round to sharing is still there later, collage intact.',
          'It ends up being a record of who you spent time with and where you went for dinners and trips with friends.',
        ],
      },
    ],
  },
];

// ── URL helpers ──────────────────────────────────────────────────────────────
// Articles live at /support/<section>/<topic-slug>. Slugs are derived from the
// visible titles, so renaming a topic changes its URL — intentional while the
// help center is young; add a redirect if a published article gets retitled.

export const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export const topicSlug = (item: TopicItem) => slugify(itemTitle(item));

export const getSection = (sectionId: string) =>
  SUPPORT_SECTIONS.find((s) => s.id === sectionId);

export const topicHref = (sectionId: string, item: TopicItem) =>
  `/support/${sectionId}/${topicSlug(item)}`;

export const sectionHref = (sectionId: string) => `/support/${sectionId}`;

export const findTopicIndex = (sectionId: string, slug: string) => {
  const sec = getSection(sectionId);
  if (!sec) return -1;
  return sec.items.findIndex((item) => topicSlug(item) === slug);
};

// Every article as a flat list — drives generateStaticParams and the sitemap.
export const allTopicParams = () =>
  SUPPORT_SECTIONS.flatMap((sec) =>
    sec.items.map((item) => ({ section: sec.id, topic: topicSlug(item) }))
  );

// Baseline revision date for help articles that don't carry their own
// `updated`. Every article was rewritten or retitled in the 2026-08-03 content
// pass, so this is honest for all 39 of them today; as individual articles get
// edited from here they should set `updated` rather than move this constant,
// which would re-stamp the whole help center as changed.
export const SUPPORT_CONTENT_REVISED = '2026-08-03';

export const articleUpdated = (item: TopicItem) =>
  new Date(itemArticle(item)?.updated ?? SUPPORT_CONTENT_REVISED);

// Latest article date in a section — what its index page actually reflects,
// since the index is a list of those articles.
// Math.max() of nothing is -Infinity, and `new Date(-Infinity)` is an Invalid
// Date that serializes into <lastmod> as garbage and makes the sitemap
// unparseable — so a section that temporarily has no articles would break the
// file rather than just its own entry.
export const sectionUpdated = (sec: { items: TopicItem[] }) =>
  sec.items.length
    ? new Date(Math.max(...sec.items.map((item) => articleUpdated(item).getTime())))
    : new Date(SUPPORT_CONTENT_REVISED);

// Every article that has a playable clip — drives the <video:video> entries in
// the sitemap. Both a video and a poster are required: Google will not index a
// video without a reachable thumbnail, so an article missing either is skipped
// rather than emitted as an entry that can only fail.
export const allVideoArticles = () =>
  SUPPORT_SECTIONS.flatMap((sec) =>
    sec.items.flatMap((item) => {
      const article = itemArticle(item);
      if (!article?.video || !article.poster) return [];
      return [{
        section: sec,
        slug: topicSlug(item),
        title: itemTitle(item),
        searchTitle: searchTitle(item),
        article,
      }];
    })
  );

// Case-insensitive every-word match over the topic title, its body copy, and
// its section's title/tagline, so "trip receipts" finds "Adding receipts &
// expenses to the trip".
export const searchTopics = (query: string) => {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [] as Array<{ sectionId: string; itemIndex: number; item: string }>;
  const results: Array<{ sectionId: string; itemIndex: number; item: string }> = [];
  for (const sec of SUPPORT_SECTIONS) {
    const sectionText = `${sec.title} ${sec.tagline || ''}`.toLowerCase();
    sec.items.forEach((item, itemIndex) => {
      const title = itemTitle(item);
      const art = itemArticle(item);
      // Index the step content and its headings too, not just `body` — most of
      // a walkthrough article's searchable text lives in its sections.
      const body = art
        ? plainText([
            ...articleParagraphs(art),
            ...(art.sections || []).map((s) => s.heading),
          ].join(' '))
        : '';
      const haystack = `${title} ${body} ${sectionText}`.toLowerCase();
      if (words.every((w) => haystack.includes(w))) {
        results.push({ sectionId: sec.id, itemIndex, item: title });
      }
    });
  }
  return results;
};
